import ModalContainer from '@/components/molecules/ModalContainer';
import CircleFillOutWithText from '@/components/molecules/WellnessStateCircle/CircleFillOutWithText';
import logoVariants from '@/assets/img/logo';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { useAppStore } from '@/store/useAppStore';
import { text } from '@/theme/colors/palettes';
import { TCortexOrganizationInsightsDTO } from '@/types/Cortex.types';
import { orderCortexRecommendationsV1, orderSkillsToFocusOnV2 } from '@/utils/cortexInsights';
import { getOrganizationDashboard } from '@/utils/queryOptions';
import {
  Box,
  HStack,
  Image,
  ModalCloseButton,
  Skeleton,
  SkeletonCircle,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { pdf } from '@react-pdf/renderer';
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import CustomButton from '@/components/atoms/CustomButton';
import { RecommendationCardV1 } from './RecommendationsV1';
import { RecommendationCardV2 } from './RecommendationsV2';
import DataPeriodSelector from './DataPeriodSelector';
import PeerSupportDashboardOverview from '@/modules/peer-support/PeerSupportDashboardOverview';
import usePeerSupportEngagement from '@/hooks/peersupport/usePeerSupportEngagement';
import { WholenessReportPdfDocument } from './WholenessReportPdfDocument';
import OrganizationLogo from '@/components/molecules/OrganizationLogo';
import { formatYYYYMPeriod } from '@/utils/stringUtils';
import useOrganizationReport from '@/hooks/organization/useOrganizationReport';
import svgFiles from '@/assets/svg/svgFiles';
import { TOrganizationReportDomainBreakdownDto } from '@/types/Organization.types';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import { EMPTY_STRING_VALUE } from '@/constants/stringVars';

const ORG_LOGO_SIZE = 54;

const SectionLabel = ({ children }: PropsWithChildren) => (
  <Text variant={'urbanistBoldRegular'} fontSize={'12px'} color={'text.mediumGray'} fontWeight={'900'}>
    {children}
  </Text>
);

const AIOrganizationOverviewModalV2 = ({
  isOpen,
  onClose,
  cortexInsightsResult,
}: {
  isOpen: boolean;
  onClose: () => void;
  cortexInsightsResult?: TCortexOrganizationInsightsDTO;
}) => {
  const { user } = useAppStore();
  const { data: organizationDashboard } = useSuspenseQuery(getOrganizationDashboard(user?.user_id));

  // Data-period state for the Peer Support Engagement section — `yyyy-m` (1-indexed,
  // no zero-pad) to match the format the peer-support hook expects.
  const [dashboardMonthData, setDashboardMonthData] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  });

  const { data: organizationReport, isLoading: isLoadingOrganizationReport } = useOrganizationReport(
    parseInt(dashboardMonthData.split('-')[0]),
    parseInt(dashboardMonthData.split('-')[1]),
  );

  const organizationName = organizationReport?.organizationName ?? '/';
  const organizationLogoUrl = organizationDashboard.logoUrl;

  // Narrow `insights` by version so each branch produces the correctly-typed cards.
  const v1Insights = cortexInsightsResult?.insightsVersion === 'V1' ? cortexInsightsResult.insights : null;
  const v2Insights = cortexInsightsResult?.insightsVersion === 'V2' ? cortexInsightsResult.insights : null;

  // Build the per-version recommendation cards as a single priority-ordered list.
  const recommendationCards = useMemo<React.JSX.Element[]>(() => {
    if (v1Insights) {
      return orderCortexRecommendationsV1(v1Insights).map((rec, i) => (
        <RecommendationCardV1 key={`${rec.category}-${i}`} recommendation={rec} expandable={false} />
      ));
    }
    if (v2Insights) {
      return orderSkillsToFocusOnV2(v2Insights).map((rec, i) => (
        <RecommendationCardV2 key={`${rec.category}-${i}`} recommendation={rec} expandable={false} />
      ));
    }
    return [];
  }, [v1Insights, v2Insights]);
  const hasRecommendations = recommendationCards.length > 0;

  const [notes, setNotes] = useState('');
  const copyrightYear = new Date().getFullYear();

  const employeesOnRoster = organizationReport?.rosterStats.employeesInRoster ?? 0;

  const orgRosterStats = {
    employeesInRoster: employeesOnRoster,
    memberCareSessions: organizationReport?.rosterStats.clinicalConsultationSessions ?? 0,
    peerSupportMembers: organizationReport?.rosterStats.peerSupportMembers ?? 0,
    screenersSubmitted: organizationReport?.rosterStats.screenersSubmitted ?? 0,
    totalSubmitters: organizationReport?.rosterStats.totalSubmitters ?? 0,
  };

  const [isDownloading, setIsDownloading] = useState(false);

  // Peer support engagement data — same hook the embedded section uses, so React Query
  // shares the cached response (no duplicate request).
  const { data: peerSupportEngagement } = usePeerSupportEngagement(dashboardMonthData);

  // Derived period label for filenames + the PDF body ("Feb 2026").
  const periodLabel = useMemo(() => {
    return formatYYYYMPeriod(dashboardMonthData);
  }, [dashboardMonthData]);

  const previousPeriodLabel = organizationReport?.dataPeriod
    ? formatYYYYMPeriod(`${organizationReport.dataPeriod.year}-${organizationReport.dataPeriod.month - 1}`)
    : 'Previous Period';
  const currentPeriodLabel = organizationReport?.dataPeriod
    ? formatYYYYMPeriod(`${organizationReport.dataPeriod.year}-${organizationReport.dataPeriod.month}`)
    : 'Current Period';

  const previousPeriodScore = organizationReport?.wholenessScore.previous ?? 0;
  const currentPeriodScore = organizationReport?.wholenessScore.current ?? 0;

  const changeSincePrevMonthLabel = organizationReport?.wholenessScore.changePercent
    ? `${organizationReport?.wholenessScore.direction === 'DOWN' ? 'Down' : 'Up'} by ${organizationReport?.wholenessScore.changePercent}%`
    : EMPTY_STRING_VALUE;
  const isChangeDirectionDown = organizationReport?.wholenessScore.direction === 'DOWN';

  const handleDownloadPdf = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const blob = await pdf(
        <WholenessReportPdfDocument
          organizationName={organizationName}
          periodLabel={periodLabel}
          selectedMonth={dashboardMonthData}
          previousPeriodWholenessScore={previousPeriodScore}
          currentPeriodWholenessScore={currentPeriodScore}
          previousPeriodLabel={previousPeriodLabel}
          currentPeriodLabel={currentPeriodLabel}
          changeSincePrevMonthLabel={changeSincePrevMonthLabel}
          isChangeDirectionDown={isChangeDirectionDown}
          domainsOfStrengthPrevious={organizationReport?.teamStrengths.previousMonth ?? []}
          domainsOfStrengthCurrent={organizationReport?.teamStrengths.currentMonth ?? []}
          domainsOfOpportunityPrevious={organizationReport?.opportunitiesForGrowth.previousMonth ?? []}
          domainsOfOpportunityCurrent={organizationReport?.opportunitiesForGrowth.currentMonth ?? []}
          stats={orgRosterStats}
          peerSupportEngagement={peerSupportEngagement}
          cortexInsightsV1={cortexInsightsResult?.insightsVersion === 'V1' ? cortexInsightsResult.insights : null}
          cortexInsightsV2={cortexInsightsResult?.insightsVersion === 'V2' ? cortexInsightsResult.insights : null}
          notes={notes}
          copyrightYear={copyrightYear}
        />,
      ).toBlob();

      // Trigger download via a temporary anchor — avoids the file-saver dep.
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const [yearStr, monthStr] = dashboardMonthData.split('-');
      const filenamePeriod = `${yearStr}-${(monthStr ?? '').padStart(2, '0')}`;
      anchor.href = url;
      anchor.download = `${organizationName} - Wholeness Report - ${filenamePeriod}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.log('e', e);
    } finally {
      setIsDownloading(false);
    }
  }, [
    isDownloading,
    organizationName,
    periodLabel,
    dashboardMonthData,
    previousPeriodScore,
    currentPeriodScore,
    organizationReport?.rosterStats,
    peerSupportEngagement,
    cortexInsightsResult,
    notes,
    copyrightYear,
  ]);

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      isBlur={false}
      width={MAX_CONTAINER_WIDTH}
      backgroundColor={'background.lightBlue'}
      closeButtonHidden
    >
      <VStack position={'relative'} gap={'24px'} paddingY={'24px'} paddingX={'6px'} paddingBottom={'40px'}>
        {/* Header */}
        <HStack width={'100%'} justifyContent={'space-between'} alignItems={'start'}>
          <VStack alignItems={'start'} gap={'10px'}>
            <HStack gap={'10px'} alignItems={'start'}>
              <OrganizationLogo logoUrl={organizationLogoUrl} logoSize={ORG_LOGO_SIZE} rounded />
              <VStack alignItems={'start'}>
                <Text variant={'loraSectionTitle'} color={'text.darkBlue'} fontSize={'28px'}>
                  {organizationName}'s Wholeness Report
                </Text>
                <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'}>
                  {formatYYYYMPeriod(dashboardMonthData)}
                </Text>
              </VStack>
            </HStack>
          </VStack>

          <HStack gap={'20px'} alignItems={'center'} marginRight={'50px'}>
            <DataPeriodSelector value={dashboardMonthData} onChange={setDashboardMonthData} />

            <CustomButton
              label={isDownloading ? 'Generating PDF…' : 'Download as PDF'}
              onClick={handleDownloadPdf}
              labelColor="text.mediumBlue"
              isLoading={isDownloading}
              disabled={isDownloading}
              height={'54px'}
            />
          </HStack>

          <ModalCloseButton
            tabIndex={-1}
            position={'absolute'}
            top={'30px'}
            size={'lg'}
            width={'24px'}
            height={'24px'}
            color={'black'}
            fontWeight={'bold'}
          />
        </HStack>

        {/* Score + Stats Row */}
        <HStack width={'100%'} gap={'24px'} alignItems={'stretch'}>
          {/* Organization's Wholeness Score */}
          <SectionCard flex={1}>
            <Text color={'text.darkBlue'} fontSize={'18px'} fontWeight={'500'} textAlign={'center'} width={'100%'}>
              Organization's Wholeness Score
            </Text>
            <HStack justifyContent={'center'} width={'100%'} gap={'30px'} marginTop={'20px'} marginBottom={'10px'}>
              <VStack gap={'10px'}>
                <CircleFillOutWithText
                  svgId="v2-previous-score"
                  percentageFilled={previousPeriodScore}
                  circleDiameter={80}
                  thickness={6}
                  percentageTextStyle={{
                    color: text.mediumGray,
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                />
                <Text variant={'urbanistMediumLarge'} color={'text.mediumGray'}>
                  {previousPeriodLabel}
                </Text>
              </VStack>

              <Image
                maxWidth={'60px'}
                height={'28px'}
                marginBottom={'24px'}
                borderRadius={'6px'}
                src={svgFiles.arrowRightLong}
              />

              <VStack gap={'10px'} position={'relative'}>
                <Text
                  zIndex={10}
                  position={'absolute'}
                  top={-3}
                  right={-5}
                  backgroundColor={organizationReport?.wholenessScore.direction === 'DOWN' ? 'extra.red' : 'extra.teal'}
                  paddingX={2}
                  paddingY={1.5}
                  borderRadius={'30px'}
                  variant={'urbanistMaximumBold12'}
                  color={'white'}
                  boxShadow={'0px 3.6px 8.99px 0px #0025481A'}
                >
                  {organizationReport?.wholenessScore.direction === 'DOWN' ? '' : '+'}{' '}
                  {organizationReport?.wholenessScore.changePercent}%
                </Text>

                <CircleFillOutWithText
                  svgId="v2-current-score"
                  percentageFilled={currentPeriodScore}
                  circleDiameter={80}
                  thickness={6}
                  percentageTextStyle={{
                    color: text.darkBlue,
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                />
                <Text variant={'urbanistMediumLarge'} color={'text.mediumBlue'}>
                  {currentPeriodLabel}
                </Text>
              </VStack>
            </HStack>

            <HStack justifyContent={'center'} width={'100%'} marginTop={'30px'} gap={1}>
              <Text variant={'urbanistMediumLarge'} color={'text.mediumBlue'}>
                Change Since Previous Month -
              </Text>
              <Text
                variant={'urbanistMediumLarge'}
                fontWeight={'bold'}
                color={isChangeDirectionDown ? 'extra.red' : 'extra.teal'}
              >
                {changeSincePrevMonthLabel}
              </Text>
            </HStack>
          </SectionCard>

          {/* Organization Stats */}
          <SectionCard flex={1}>
            <VStack flex={1} gap={0} width={'full'} justifyContent={'space-between'}>
              <StatRow label="Employees on Roster" value={employeesOnRoster} />
              <StatRow label="# of Screeners Submitted" value={orgRosterStats.screenersSubmitted} />
              <StatRow label="Peer Support Members" value={orgRosterStats.peerSupportMembers} />
              <StatRow label="Member Care Contacts" value={orgRosterStats.totalSubmitters} />
              <StatRow label="Sessions Utilized" value={orgRosterStats.memberCareSessions} />
            </VStack>
          </SectionCard>
        </HStack>

        {/* Strengths & Opportunities Row */}
        <HStack width={'100%'} gap={'24px'} alignItems={'stretch'}>
          {/* Domains of Strength */}
          <SectionCard flex={1}>
            <Text color={'text.darkBlue'} fontSize={'18px'} fontWeight={'500'} textAlign={'left'} width={'100%'}>
              Team Strengths
            </Text>

            <HStack justifyContent={'space-between'} alignItems={'start'} gap={'24px'} width={'100%'}>
              <VStack width={'100%'} alignItems={'start'} gap={'14px'} marginTop={'12px'}>
                <SectionLabel>PREVIOUS MONTH</SectionLabel>
                {isLoadingOrganizationReport ? (
                  [1, 2, 3].map((item) => <ScoreItemLoading key={item} />)
                ) : organizationReport?.teamStrengths.previousMonth &&
                  organizationReport?.teamStrengths.previousMonth.length > 0 ? (
                  organizationReport?.teamStrengths.previousMonth.map((d) => (
                    <ScoreItem
                      key={d.domainKey}
                      domainName={d.domainName}
                      domainKey={`strengths-${d.domainKey}`}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                      isPrevious
                    />
                  ))
                ) : (
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    Not enough screener data yet.
                  </Text>
                )}
              </VStack>
              <VStack width={'100%'} alignItems={'start'} gap={'14px'} marginTop={'12px'}>
                <SectionLabel>SELECTED MONTH</SectionLabel>
                {isLoadingOrganizationReport ? (
                  [1, 2, 3].map((item) => <ScoreItemLoading key={item} />)
                ) : organizationReport?.teamStrengths.currentMonth &&
                  organizationReport?.teamStrengths.currentMonth.length > 0 ? (
                  organizationReport?.teamStrengths.currentMonth.map((d) => (
                    <ScoreItem
                      key={d.domainKey}
                      domainName={d.domainName}
                      domainKey={`strengths-${d.domainKey}`}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    {isLoadingOrganizationReport ? ' Loading...' : 'Not enough screener data yet.'}
                  </Text>
                )}
              </VStack>
            </HStack>
          </SectionCard>

          {/* Opportunities for Growth */}
          <SectionCard flex={1}>
            <Text color={'text.darkBlue'} fontSize={'18px'} fontWeight={'500'} textAlign={'left'} width={'100%'}>
              Opportunities for Growth
            </Text>

            <HStack justifyContent={'space-between'} alignItems={'start'} gap={'24px'} width={'100%'}>
              <VStack width={'100%'} alignItems={'start'} gap={'14px'} marginTop={'12px'}>
                <SectionLabel>PREVIOUS MONTH</SectionLabel>
                {isLoadingOrganizationReport ? (
                  [1, 2, 3].map((item) => <ScoreItemLoading key={item} />)
                ) : organizationReport?.opportunitiesForGrowth.previousMonth &&
                  organizationReport?.opportunitiesForGrowth.previousMonth.length > 0 ? (
                  organizationReport?.opportunitiesForGrowth.previousMonth.map((d) => (
                    <ScoreItem
                      key={d.domainKey}
                      domainName={d.domainName}
                      domainKey={`opportunities-${d.domainKey}`}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                      isPrevious
                    />
                  ))
                ) : (
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    {isLoadingOrganizationReport ? ' Loading...' : 'Not enough screener data yet.'}
                  </Text>
                )}
              </VStack>
              <VStack width={'100%'} alignItems={'start'} gap={'14px'} marginTop={'12px'}>
                <SectionLabel>CURRENT MONTH</SectionLabel>
                {isLoadingOrganizationReport ? (
                  [1, 2, 3].map((item) => <ScoreItemLoading key={item} />)
                ) : organizationReport?.opportunitiesForGrowth.currentMonth &&
                  organizationReport?.opportunitiesForGrowth.currentMonth.length > 0 ? (
                  organizationReport?.opportunitiesForGrowth.currentMonth.map((d) => (
                    <ScoreItem
                      key={d.domainKey}
                      domainName={d.domainName}
                      domainKey={`opportunities-${d.domainKey}`}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    {isLoadingOrganizationReport ? ' Loading...' : 'Not enough screener data yet.'}
                  </Text>
                )}
              </VStack>
            </HStack>
          </SectionCard>
        </HStack>

        {/* Peer Support Engagement Data */}
        <SectionCard width="100%">
          <PeerSupportDashboardOverview
            embedded
            canLogContact={false}
            month={dashboardMonthData}
            onMonthChange={setDashboardMonthData}
          />
        </SectionCard>

        {/* MyOmnia Recommends + Notes Row */}
        <HStack width={'100%'} gap={'24px'} alignItems={'stretch'}>
          {/* MyOmnia Recommends */}
          <SectionCard flex={1}>
            <Text color={'text.darkBlue'} fontSize={'18px'} fontWeight={'500'} width={'100%'}>
              MyOmnia Recommends
            </Text>
            <SectionLabel>SKILLS TO FOCUS ON</SectionLabel>

            {hasRecommendations ? (
              <VStack width={'100%'} gap={'12px'} marginTop={'8px'} alignItems={'stretch'}>
                {recommendationCards}
              </VStack>
            ) : (
              <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} width={'100%'}>
                Recommendations are still being generated.
              </Text>
            )}
          </SectionCard>

          {/* Notes */}
          <SectionCard flex={1}>
            <Text color={'text.darkBlue'} fontSize={'18px'} fontWeight={'500'} width={'100%'}>
              Notes
            </Text>
            <VStack
              flex={1}
              minHeight={'200px'}
              borderWidth={'1px'}
              borderColor={'extra.mediumGray30pct'}
              borderRadius={'8px'}
              padding={'10px 18px'}
              marginTop={'8px'}
              width={'full'}
              backgroundColor={'white'}
            >
              <Textarea
                tabIndex={-1}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={'Write notes down here'}
                variant={'unstyled'}
                flex={1}
                minHeight={'200px'}
                resize={'none'}
                padding={'8px 0'}
                fontFamily={'inherit'}
                _placeholder={{ color: 'text.mediumGray' }}
              />
            </VStack>
          </SectionCard>
        </HStack>

        {/* Footer */}
        <HStack width={'100%'} justifyContent={'center'} alignItems={'center'} paddingTop={'8px'}>
          <HStack gap={'10px'} alignItems={'center'}>
            <Image
              src={logoVariants.purple252x80}
              alt={'MyOmnia'}
              height={'30px'}
              width={'auto'}
              objectFit={'contain'}
            />
            <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
              Copyright © {copyrightYear}
            </Text>
          </HStack>
        </HStack>
      </VStack>
    </ModalContainer>
  );
};

export default AIOrganizationOverviewModalV2;

// --- Sub-components ---

const SectionCard = ({ children, flex, width }: PropsWithChildren<{ flex?: number; width?: string }>) => (
  <VStack
    position={'relative'}
    backgroundColor={'rgba(255, 255, 255, 0.75)'}
    borderRadius={'12px'}
    padding={'14px 24px'}
    gap={'8px'}
    alignItems={'start'}
    boxShadow={'0px 8px 40px 0px #0041790D'}
    flex={flex}
    width={width}
    border={'1px solid'}
    borderColor={'background.cloudGray'}
  >
    {children}
  </VStack>
);

const StatRow = ({ label, value }: { label: string; value: number }) => (
  <HStack width={'100%'} justifyContent={'space-between'} paddingY={'16px'}>
    <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumGray'} fontSize={'16px'}>
      {label}
    </Text>
    <Text variant={'urbanistBold'} color={'text.mediumBlue'} fontSize={'24px'} fontWeight={'800'}>
      {value}
    </Text>
  </HStack>
);

const ScoreItemLoading = () => {
  return (
    <HStack paddingBottom={'8px'}>
      <SkeletonCircle size="40px" startColor="gray.100" endColor="gray.300" />
      <Skeleton height="14px" width={'200px'} startColor="gray.100" endColor="gray.300" borderRadius="6px" />
    </HStack>
  );
};

const ScoreItem = ({
  domainName,
  domainKey,
  score,
  subdomains,
  isPrevious,
}: TOrganizationReportDomainBreakdownDto & { isPrevious?: boolean }) => (
  <VStack align={'start'}>
    <HStack gap={'14px'} alignItems={'center'}>
      <CircleFillOutWithText
        svgId={`v2-score-${domainKey}-${domainName.split(' ')[0].toLowerCase()}-${score}-${isPrevious ? 'previous' : ''}`}
        percentageFilled={score}
        circleDiameter={40}
        thickness={4}
        hasBackgroundCircleFillOut
        boxShadow="none"
        percentageTextStyle={{
          color: text.darkBlue,
          fontWeight: 700,
          fontSize: 14,
        }}
      />
      <Text variant={'urbanistMediumLarge'} color={'text.darkBlue'}>
        {domainName}
      </Text>
    </HStack>

    <VStack paddingLeft={'15px'} align={'start'}>
      {subdomains?.map((subdomain) => (
        <HStack key={subdomain.name}>
          <Box backgroundColor={getColorFromPercentage(subdomain.score)} w="10px" h="10px" borderRadius={'50%'} />
          <Text variant={'urbanistBold'}>{subdomain.score}</Text>
          <Text variant={'urbanistMedium'}>{subdomain.name}</Text>
        </HStack>
      ))}
    </VStack>
  </VStack>
);
